/**
 * Deterministic voice command matcher — no LLM, no network, pure string logic.
 * Maps spoken capability commands to per-turn patch objects that the voice mode
 * hook applies before sending a turn. Commands are case-insensitive, prefix-matched
 * after normalization (trim, strip filler words). When the transcript matches a
 * command the caller applies the patch and speaks a short confirmation; when it
 * doesn't match, the transcript is sent as a normal chat message.
 */

const FILLER = /^(?:hey|okay|ok|so|uh|um|like|please|can you|could you|would you)[, ]*/i;

function normalize(transcript) {
  let text = transcript.trim().replace(FILLER, '').toLowerCase();
  // Strip trailing punctuation
  while (text.length && '.!?'.includes(text[text.length - 1])) text = text.slice(0, -1);
  return text;
}

/**
 * Each rule: { pattern: RegExp, type: string, patch: object, confirm: string }
 * `type` is a stable key for deduplication / logging; `patch` is the per-turn
 * override to merge into the send payload; `confirm` is spoken back to the user.
 */
const RULES = [
  // Approval mode
  { pattern: /^(?:auto[- ]?approve|auto approve) (?:on|enable|yes|start)/, type: 'approval', patch: { approvalMode: 'auto' }, confirm: 'Auto-approve enabled.' },
  { pattern: /^(?:auto[- ]?approve|auto approve) (?:off|disable|no|stop)/, type: 'approval', patch: { approvalMode: 'attended' }, confirm: 'Auto-approve disabled.' },
  { pattern: /^(?:switch to|use) (?:attended|manual|approval)/, type: 'approval', patch: { approvalMode: 'attended' }, confirm: 'Switched to manual approval.' },
  { pattern: /^(?:switch to|use) auto(?:[- ]?approve)?/, type: 'approval', patch: { approvalMode: 'auto' }, confirm: 'Switched to auto-approve.' },

  // Retrieval
  { pattern: /^(?:search|use retrieval|retrieve|look (?:up|into)|check (?:the )?(?:docs|knowledge|kb|knowledge base))/, type: 'retrieval', patch: { retrievalEnabled: true }, confirm: 'Retrieval enabled.' },
  { pattern: /^(?:no retrieval|skip retrieval|don'?t search|disable retrieval|stop searching|off retrieval)/, type: 'retrieval', patch: { retrievalEnabled: false }, confirm: 'Retrieval disabled.' },

  // Tools
  { pattern: /^(?:no tools|skip tools|don'?t use tools|disable tools|answer directly|use your own knowledge)/, type: 'tools', patch: { toolsEnabled: false }, confirm: 'Tools disabled — answering from my own knowledge.' },
  { pattern: /^(?:use tools|tools on|enable tools|tools on)/, type: 'tools', patch: { toolsEnabled: true }, confirm: 'Tools enabled.' },
];

/**
 * Match a spoken transcript against the command table.
 * @param {string} transcript — raw STT output
 * @param {object} [currentApprovalMode] — current approval mode for toggle detection
 * @returns {{ type: string, patch: object, confirm: string } | null}
 */
function matchVoiceCommand(transcript) {
  const text = normalize(transcript);
  if (!text) return null;
  for (const rule of RULES) {
    if (rule.pattern.test(text)) return rule;
  }
  return null;
}

/**
 * Fold a matched command's capability patch into the turn's accumulated patch.
 * Only the capability keys ride the next send — approvalMode is applied to state
 * immediately instead, so it is deliberately not carried here.
 *
 * @param {object} current — the patch accumulated so far this turn
 * @param {object} patch — the matched rule's patch
 * @returns {object} a new accumulated patch
 */
function mergeCapabilityPatch(current, patch) {
  const next = { ...current };
  if (patch?.retrievalEnabled != null) next.retrievalEnabled = patch.retrievalEnabled;
  if (patch?.toolsEnabled != null) next.toolsEnabled = patch.toolsEnabled;
  return next;
}

export { matchVoiceCommand, normalize, mergeCapabilityPatch };
